import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account, displayAccountSyncStatus } from '@/client/accounts';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { CYBERPEDIA_AD_AND_SSO_URL } from '@/common/constants/external-urls';
import { TranslationFunctionType } from '@/common/types';
import { AzureADForm } from '@/components/accounts/forms/AzureADForm';
import { EntraIdSyncSettings } from '@/components/accounts/forms/EntraIdSyncSettings';
import { SSOForm } from '@/components/accounts/forms/SSOForm';
import { useAzureADHandler } from '@/hooks/integrations/useAzureAD.hook';
import { useSSOHandler } from '@/hooks/integrations/useSSOConfig.hook';
import { useAccount } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { InlineMenu } from '@/ui/inline-menu';
import { FlexContainer } from '@/ui/styled-ui';
import { isDemoRestrictedMode } from '@/utils/helpers';
import moment from 'moment';
import { MenuItem } from 'primereact/menuitem';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AccountHeader } from './AccountHeader';

const StyledGuideLink = styled.a`
  display: inline-block;
  border: none;
  border-bottom: 2px solid var(--red-main);
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: var(--purple-dark);
  text-decoration: none;
  margin-bottom: var(--medium-big-padding);
  margin-top: var(--xxsmall-padding);
`;

const StyledMessage = styled(Message)`
  display: flex;
  margin-bottom: var(--medium-big-padding);
`;

const getPathItems = (
  account: Account | undefined,
  currentAccount: Account,
  can: (action: Actions, subject: Subjects) => boolean,
  t: TranslationFunctionType,
): MenuItem[] => {
  const pathItems: MenuItem[] = [];

  pathItems.push({
    label: currentAccount?.name,
    url: !currentAccount.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](currentAccount?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
    template: AppBreadCrumbTemplate,
  });

  if (
    currentAccount?.isSystem &&
    account &&
    can(Actions.READ, Subjects.ACCOUNTS)
  ) {
    pathItems.push(
      {
        label: t('accounts'),
        url: RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
      {
        label: account?.name,
        url: !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        className: 'active',
        template: AppBreadCrumbTemplate,
      },
    );
  }

  pathItems.push({
    label: t('microsoft'),
    url: !currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS_MICROSOFT]()
      : RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTEGRATIONS_MICROSOFT](
          account ? account?.id : currentAccount?.id,
        ),
    template: AppBreadCrumbTemplate,
  });

  return pathItems;
};

export const AccountMicrosoftPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const { can } = usePermission();
  const { account, isLoading: isAccountLoading } = useAccount({
    accountId: currentAccount?.isSystem ? id : currentAccount?.id,
  });

  const { initialValues, isLoading, handleSubmit } = useSSOHandler(
    account?.id as string,
  );
  const {
    initialValues: initialValuesAD,
    syncState,
    handleSubmit: handleSubmitAzureAD,
    handleForceADSync,
    isADLoading,
    accountAd,
  } = useAzureADHandler(account?.id as string);

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isDirty, setIsDirty] = useState(false);
  const azureAdSubmitRef = useRef<(() => Promise<boolean | void>) | null>(null);
  const ssoSubmitRef = useRef<(() => Promise<boolean | void>) | null>(null);
  const entraSubmitRef = useRef<(() => Promise<boolean | void>) | null>(null);
  const isMicrosoftIntegrationDisabled =
    account?.isSystem && currentUser?.role.code !== SystemRoles.DEVELOPER;
  const isDemoAccount = isDemoRestrictedMode(account ?? null, currentUser);
  const secretNeedsUpdate =
    !accountAd?.config?.hasClientSecret ||
    /expired/i.test(accountAd?.meta?.lastSyncErrorLog ?? '');

  const menuItems = [
    { label: t('generic.configuration') },
    { label: t('sso') },
    { label: t('azure.entra.syncSettings') },
  ];

  const items = menuItems.map((item, index) => ({
    ...item,
    className: activeIndex === index ? 'active' : '',
    command: () => {
      setActiveIndex(index);
      setIsDirty(false);
    },
  }));

  return (
    <>
      {isLoading || isAccountLoading || !account || isADLoading ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          {currentAccount && (
            <AppBreadCrumb
              model={getPathItems(account, currentAccount, can, t)}
            />
          )}
          <AccountHeader
            selectedAccount={account}
            isDirty={isDirty}
            onSave={async () => {
              if (activeIndex === 0) return azureAdSubmitRef.current?.();
              if (activeIndex === 1) return ssoSubmitRef.current?.();
              return entraSubmitRef.current?.();
            }}
          />
          {accountAd?.meta?.lastSync && accountAd?.meta?.status && (
            <StyledMessage
              severity="info"
              text={`${t('sync.lastSync')} ${moment(
                accountAd?.meta?.lastSync,
              ).format(DateFormats.TIMEDATE)}, ${displayAccountSyncStatus(
                accountAd?.meta?.status,
                t,
              )}`}
            />
          )}
          <FlexContainer
            className="mb-5"
            gap="var(--big-padding)"
            justify="flex-start"
            align="flex-start"
          >
            <InlineMenu items={items} />
            <div className="w-full">
              {activeIndex === 0 && (
                <>
                  <>
                    <h3 className="mt-0">{t('account.microsoft.config')}</h3>
                    <p className="m-0">
                      {t(
                        'account.microsoft.integration.configuration.description',
                        {
                          platformName: account?.platformSettings?.name,
                        },
                      )}
                    </p>
                    <StyledGuideLink
                      href={CYBERPEDIA_AD_AND_SSO_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('azure.entra.guide.url')}
                    </StyledGuideLink>
                    <AzureADForm
                      initialValues={initialValuesAD}
                      onSubmit={handleSubmitAzureAD}
                      currentClientSecret={initialValuesAD.clientSecret}
                      onSync={() => handleForceADSync(account?.id as string)}
                      account={account}
                      syncState={syncState}
                      disabled={
                        account?.isSystem &&
                        currentUser?.role.code !== SystemRoles.DEVELOPER
                      }
                      secretNeedsUpdate={secretNeedsUpdate}
                      onDirtyChange={setIsDirty}
                      submitRef={azureAdSubmitRef}
                    />
                  </>
                </>
              )}
              {activeIndex === 1 && (
                <>
                  <h3 className="mt-0">{t('sso.config')}</h3>
                  <p className="m-0">
                    {t('account.microsoft.integration.sso.description', {
                      platformName: account?.platformSettings?.name,
                    })}
                  </p>
                  <StyledGuideLink
                    href={CYBERPEDIA_AD_AND_SSO_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('azure.entra.guide.url')}
                  </StyledGuideLink>
                  <SSOForm
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    account={account}
                    disabled={
                      !account?.subdomain || isMicrosoftIntegrationDisabled
                    }
                    onDirtyChange={setIsDirty}
                    submitRef={ssoSubmitRef}
                  />
                </>
              )}
              {activeIndex === 2 && (
                <>
                  <h3 className="mt-0">
                    {t('azure.entra.syncSettings.title')}
                  </h3>
                  <p className="mt-0">
                    {t('azure.entra.syncSettings.description', {
                      platformName: account?.platformSettings?.name,
                    })}
                  </p>
                  <EntraIdSyncSettings
                    account={account}
                    disabled={!!isMicrosoftIntegrationDisabled}
                    isDemoAccount={isDemoAccount}
                    adConfig={accountAd}
                    onSync={() => handleForceADSync(account?.id as string)}
                    syncState={syncState}
                    onDirtyChange={setIsDirty}
                    saveRef={entraSubmitRef}
                  />
                </>
              )}
            </div>
          </FlexContainer>
        </>
      )}
    </>
  );
};
